// @ts-nocheck
import { Cart, CartResult, UpdateCartApiParameter } from "@insite/client-framework/Services/CartService";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCart";
import {
    getCurrentPromotionsDataView,
    getOrderPromotions,
} from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import {
    chain,
    DispatchCompletePlaceOrder,
    DispatchResetOrders,
    ExecuteOnSuccessCallback,
    SendTracking,
    SetCartStatus,
} from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/PlaceOrder";
import { dispatchCheckoutEvent, dispatchPurchaseEvent } from "@store/Services/AnalyticsService";
import {
    addToChainAfter,
    ApiHandlerDiscreteParameter,
    HasOnError,
    markSkipOnCompleteIfOnSuccessIsSet,
    replaceInChain,
} from "@toolkit/Modules/client-framework/HandlerCreator";
import cloneDeep from "lodash/cloneDeep";

interface PlaceOrderParameter {
    paymentMethod: string;
    poNumber: string;
    vatNumber?: string;
    saveCard: boolean;
    cardHolderName: string;
    cardNumber: string;
    cardType: string;
    expirationMonth: number;
    expirationYear: number;
    securityCode: string;
    useBillingAddress: boolean;
    address1: string;
    countryId: string;
    stateId: string;
    city: string;
    postalCode: string;
    onSuccess?: (cartId: string) => void;
    payPalToken?: string;
    payPalPayerId?: string;
    isPending?: boolean;
    isAdyenDropIn?: boolean;
    adyenPspReference?: string;
    accountHolderName?: string;
    accountNumber?: string;
    routingNumber?: string;
}

type HandlerType = ApiHandlerDiscreteParameter<
    PlaceOrderParameter & HasOnError,
    UpdateCartApiParameter,
    CartResult,
    {
        cartToUpdate: Cart;
    }
>;

export const StoreSetCartStatus: HandlerType = props => {
    const state = props.getState(); // Store Custom Code
    const { cartId } = state.pages.checkoutReviewAndSubmit;
    const cart = cartId ? getCartState(state, cartId).value : getCurrentCartState(state).value;
    const notes = state.pages.checkoutShipping.editedCartNotes || ""; // Store Custom Code
    const notifyStoreManager = state.store.pages.checkoutReviewAndSubmit.notifyStoreManager?.toString() || "false"; // Store Custom Code
    const outOfCycleRefusal = state.store.pages.checkoutReviewAndSubmit.outOfCycleRefusal?.toString() || "false"; // Store Custom Code
    const orderReason = JSON.stringify(state.store.pages.cart.orderReason); // Store Custom Code

    if (!cart) {
        throw new Error("There was no current cart and we are trying to place the current cart as an order.");
    }

    const newStatus = cart?.requiresApproval
        ? "AwaitingApproval"
        : props.parameter?.isPending
        ? "PendingPaymentValidation"
        : "Submitted";

    props.cartToUpdate = {
        ...cloneDeep(cart),
        status: newStatus,
        poNumber: props.parameter.poNumber,
        customerVatNumber: props.parameter.vatNumber || "",
        notes, // Store Custom Code
        properties: {
            ...cart.properties,
            NotifyManagementOnSubmit: notifyStoreManager,
            IsApprovedForInCycleOnly: outOfCycleRefusal,
            orderReason,
        }, // Store Custom Code
    };
};

export const ResetLinkedCart: HandlerType = props => {
    const state = props.getState();
    const cart = getCartState(state, props.apiResult.cart.id).value;

    if (cart) {
        props.dispatch({
            type: "Data/Carts/ResetCart",
            id: cart.id,
        });
    }
};

export const StoreSetClearNotes: HandlerType = props => {
    props.dispatch({
        type: "Pages/CheckoutShipping/SetNotes",
        notes: "",
    });
};

export const ResetOrderReason: HandlerType = props => {
    props.dispatch({
        type: "store/Pages/Cart/ResetOrderReason",
    });
};

// Store Custom Code
export const StoreSendTracking: HandlerType = props => {
    // Do not do built-in tracking.
};

export const StoreExecuteOnSuccessCallback: HandlerType = props => {
    markSkipOnCompleteIfOnSuccessIsSet(props);
    /* Store Custom Code - Start */
    if (props.apiResult.cart.properties.linkedCarts) {
        props.dispatch(loadCart({ cartId: props.apiResult.cart.properties.linkedCarts }));
    }
    /* Store Custom Code - End */
    props.parameter.onSuccess?.(props.apiResult?.cart?.id);
    /* Store Custom Code - Start */
    dispatchCheckoutEvent(2, props.cartToUpdate, props.parameter.paymentMethod);
    const state = props.getState();
    const currentPromotionsState = getCurrentPromotionsDataView(state);
    let orderPromotion;
    if (currentPromotionsState.value) {
        orderPromotion = getOrderPromotions(currentPromotionsState.value);
    }

    const orderNumber = props.apiResult.cart.erpOrderNumber || props.apiResult.cart.orderNumber;

    if (orderPromotion && orderPromotion.length && orderPromotion[0].name) {
        dispatchPurchaseEvent(orderNumber, props.cartToUpdate, props.apiResult?.billTo, orderPromotion[0].name);
    } else {
        dispatchPurchaseEvent(orderNumber, props.cartToUpdate, props.apiResult?.billTo);
    }
    /* Store Custom Code - End */
};

replaceInChain(chain, SetCartStatus, StoreSetCartStatus);
addToChainAfter(chain, DispatchCompletePlaceOrder, StoreSetClearNotes);
addToChainAfter(chain, DispatchCompletePlaceOrder, ResetOrderReason);

replaceInChain(chain, SendTracking, StoreSendTracking); // Store Custom Code
replaceInChain(chain, ExecuteOnSuccessCallback, StoreExecuteOnSuccessCallback); // Store Custom Code

addToChainAfter(chain, DispatchResetOrders, ResetLinkedCart);
